// components/FlexibleBarChart.jsx
import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useChartExport } from '../hooks/useChartExport';
import ExportPopover from './ExportPopover';

// Color schemes for different chart types
const COLOR_SCHEMES = {
  blues: ['#A3CAFA', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8'],
  greens: ['#6EE7B7', '#34D399', '#10B981', '#059669', '#047857'],
  purples: ['#C4B5FD', '#A78BFA', '#8B5CF6', '#7C3AED', '#6D28D9'],
  oranges: ['#FDBA74', '#FB923C', '#F97316', '#EA580C', '#C2410C'],
  equity: ['#A3CAFA', '#E1E3E5'],
  categorical: ['#60A5FA', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'],
  nivo: ['#e8c1a0', '#f47560', '#f1e15b', '#e8a838', '#61cdbb', '#97e3d5'],
};

// Helper: split text into lines for word wrapping
function splitTextOnWords(text, maxLength) {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + ' ' + word).trim().length > maxLength) {
      if (currentLine) lines.push(currentLine.trim());
      currentLine = word;
    } else {
      currentLine += (currentLine ? ' ' : '') + word;
    }
  });
  if (currentLine) lines.push(currentLine.trim());
  return lines;
}

// Helper: format values for display
const formatValue = (value, isPercent = false) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    // Check if value is a whole number
    const isWholeNumber = Number.isInteger(value);

    if (isPercent) {
      // For percentages: show decimal only if not whole
      return isWholeNumber ? `${value}%` : `${value.toFixed(1)}%`;
    }

    // For large numbers, abbreviate
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;

    // For regular numbers: show as whole if integer, otherwise 1 decimal
    return isWholeNumber ? String(value) : value.toFixed(1);
  }
  return String(value);
};

// Chart theme
const CHART_THEME = {
  fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
  grid: {
    line: {
      stroke: '#a3a3a3',
      strokeWidth: 0.5,
      strokeDasharray: '6,4',
    },
  },
  axis: {
    domain: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
      text: {
        fill: '#333333',
        fontSize: '0.875rem',
        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      },
    },
    legend: {
      text: {
        fill: '#333333',
        fontSize: '1rem',
        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      },
    },
  },
  legends: {
    text: {
      fill: '#333333',
      fontSize: '0.875rem',
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    },
  },
};

export default function FlexibleBarChart({
  data,
  keys,
  title,
  indexBy = 'label',
  colorScheme = 'blues',
  colors: customColors,
  layout = 'vertical',
  groupMode = 'grouped',
  showLabels = true,
  minValue = 0,
  maxValue = 'auto',
  yAxisLabel,
  xAxisLabel,
  valueFormat,
  showLegend = true,
  valueType = 'auto', // 'percent', 'number', or 'auto' (auto-detects based on values)
  categoryLegend, // Optional: { "IT Careers": "#60A5FA", "Trades": "#F59E0B" } for custom category legend
}) {
  // Prepare data for clipboard export
  const prepareDataForClipboard = (chartData) => {
    const lines = [];
    if (title) lines.push(`Title: ${title}`);
    lines.push(keys.join('\t'));
    chartData.forEach((row) => {
      const values = keys.map((k) => row[k] ?? '');
      lines.push(`${row[indexBy]}\t${values.join('\t')}`);
    });
    return lines.join('\n');
  };

  // Use the chart export hook
  const {
    chartRef,
    exportChartAsPNG,
    exportChartToExcel,
    copyImageToClipboard,
    copyDataToClipboard,
  } = useChartExport({
    defaultFileName: title ? title.replace(/\s+/g, '_').toLowerCase() : 'bar_chart',
    chartData: data,
    headers: [indexBy, ...keys],
    prepareDataForClipboard,
  });

  // Determine if values should be displayed as percentages
  const isPercentage = () => {
    if (valueType === 'percent') return true;
    if (valueType === 'number') return false;
    // Auto-detect: if all values are <= 100 and yAxisLabel contains '%' or 'percent', treat as percent
    const allValues = data.flatMap(d => keys.map(k => d[k])).filter(v => v != null);
    const maxVal = Math.max(...allValues);
    const looksLikePercent = maxVal <= 100 && (
      yAxisLabel?.toLowerCase().includes('%') ||
      yAxisLabel?.toLowerCase().includes('percent') ||
      keys.some(k => k.toLowerCase().includes('%'))
    );
    return looksLikePercent;
  };

  const showAsPercent = isPercentage();

  // Resolve colors from scheme or custom
  // Supports coloring by measure (id) OR by category (indexValue)
  const resolveColors = () => {
    if (customColors && typeof customColors === 'object' && !Array.isArray(customColors)) {
      // Object mapping - check if keys match measures or categories
      return ({ id, indexValue }) => {
        // First check if color is defined for this category (indexValue)
        if (indexValue && customColors[indexValue]) {
          return customColors[indexValue];
        }
        // Then check if color is defined for this measure (id/key)
        if (customColors[id]) {
          return customColors[id];
        }
        return '#A3CAFA';
      };
    }
    if (customColors && Array.isArray(customColors)) {
      // Array of colors - assign by key (measure), not by bar index
      return ({ id }) => {
        const keyIndex = keys.indexOf(id);
        return customColors[keyIndex >= 0 ? keyIndex % customColors.length : 0];
      };
    }
    // Use color scheme
    const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.blues;
    if (Array.isArray(scheme)) {
      // Assign colors by key (measure), not by bar index
      return ({ id }) => {
        const keyIndex = keys.indexOf(id);
        return scheme[keyIndex >= 0 ? keyIndex % scheme.length : 0];
      };
    }
    return ({ id }) => scheme[id] || '#A3CAFA';
  };

  // Title layer with word wrapping
  const titleLayer = ({ width, margin }) => {
    if (!title) return null;

    const chartWidth = width - margin.left - margin.right;
    const maxChars = Math.floor(chartWidth / 9);
    const lines = splitTextOnWords(title, maxChars);
    const fontSize = lines.length > 2 ? '0.95rem' : '1.15rem';
    const xCenter = chartWidth / 2;
    const yPos = -margin.top + 25;

    return (
      <g>
        <text
          x={xCenter}
          y={yPos}
          textAnchor="middle"
          style={{
            fontSize,
            fontWeight: 'bold',
            fill: '#333333',
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          }}
        >
          {lines.map((line, i) => (
            <tspan key={i} x={xCenter} dy={i === 0 ? 0 : '1.3em'}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  // Custom grid layer
  const gridLayer = ({ yScale, width }) => {
    const ticks = yScale.ticks ? yScale.ticks(5) : yScale.domain();
    return (
      <g>
        {ticks.map((tick, i) => (
          <line
            key={i}
            x1={0}
            x2={width}
            y1={yScale(tick)}
            y2={yScale(tick)}
            stroke="#a3a3a3"
            strokeWidth={0.5}
            strokeDasharray="6,4"
          />
        ))}
      </g>
    );
  };

  // Labels layer (above bars)
  const labelsLayer = ({ bars }) => {
    if (!showLabels) return null;

    return (
      <g>
        {bars.map((bar) => {
          const value = bar.data.value;
          if (value === null || value === undefined) return null;

          const labelText = valueFormat
            ? valueFormat(value)
            : formatValue(value, showAsPercent);
          const labelWidth = Math.max(labelText.length * 8, 36);

          return (
            <g key={bar.key}>
              {/* White background for label */}
              <rect
                x={bar.x + bar.width / 2 - labelWidth / 2}
                y={bar.y - 20}
                width={labelWidth}
                height={16}
                fill="#ffffff"
                rx={2}
              />
              {/* Label text */}
              <text
                x={bar.x + bar.width / 2}
                y={bar.y - 7}
                textAnchor="middle"
                style={{
                  fill: '#4B5563',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                }}
              >
                {labelText}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  // Tooltips layer
  const tooltipLayer = ({ bars }) => {
    // Helper to find category label from categoryLegend based on bar color
    const getCategoryLabel = (barColor, indexValue) => {
      if (categoryLegend) {
        // First check if indexValue matches a color in categoryLegend via customColors
        if (customColors && customColors[indexValue]) {
          // Find the category label that has this color
          for (const [label, color] of Object.entries(categoryLegend)) {
            if (color === customColors[indexValue]) {
              return label;
            }
          }
        }
        // Fallback: find category by matching bar color
        for (const [label, color] of Object.entries(categoryLegend)) {
          if (color === barColor) {
            return label;
          }
        }
      }
      return null;
    };

    return (
      <g>
        {bars.map((bar) => {
          const value = bar.data.value;
          const indexValue = bar.data.indexValue;
          const seriesId = bar.data.id;

          // Get category label if using categoryLegend, otherwise use seriesId
          const categoryLabel = getCategoryLabel(bar.color, indexValue);
          const displayLabel = categoryLabel || seriesId;

          const tooltipContent = (
            <div className="text-slate-700 rounded-md px-3 py-2 bg-zinc-50 border-white border-2 outline outline-zinc-100 outline-4 -outline-offset-1">
              <div className="font-semibold text-slate-800 mb-1">{indexValue}</div>
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: bar.color }}
                />
                <span className="font-medium">{displayLabel}:</span>
                <span>{formatValue(value, showAsPercent)}</span>
              </div>
            </div>
          );

          return (
            <Tippy
              key={bar.key}
              content={tooltipContent}
              placement="top"
              offset={[0, 30]}
              appendTo={document.body}
              interactive={false}
              delay={[100, 0]}
              arrow={true}
              theme="industryTrends"
            >
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill="transparent"
                className="cursor-pointer"
              />
            </Tippy>
          );
        })}
      </g>
    );
  };

  // Calculate max value for proper scaling
  const computedMaxValue = maxValue === 'auto'
    ? Math.ceil(Math.max(...data.flatMap(d => keys.map(k => d[k] || 0))) / 10) * 10 || 100
    : maxValue;

  // Custom category legend layer (for coloring by category instead of measure)
  const categoryLegendLayer = ({ width }) => {
    if (!categoryLegend) return null;

    const entries = Object.entries(categoryLegend);
    // Dynamic width based on longest label (~8px per char + symbol padding)
    const maxLabelLength = Math.max(...entries.map(([label]) => label.length));
    const itemWidth = Math.max(80, maxLabelLength * 8 + 30);
    const itemSpacing = 20;
    const totalWidth = entries.length * itemWidth + (entries.length - 1) * itemSpacing;
    const startX = (width - totalWidth) / 2;
    const yOffset = -25;

    return (
      <g>
        {entries.map(([label, color], i) => {
          const xPos = startX + i * (itemWidth + itemSpacing);
          return (
            <g key={label} transform={`translate(${xPos}, ${yOffset})`}>
              <circle cx={8} cy={0} r={7} fill={color} />
              <text
                x={22}
                y={4}
                style={{
                  fill: '#333333',
                  fontSize: '0.875rem',
                  fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                }}
              >
                {label}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  // Count number of data items for adaptive layout
  const itemCount = data.length;
  const needsRotatedLabels = itemCount > 6;

  // Custom bottom axis tick with rotation for many items
  const axisBottomTick = ({ value, x, y, textAnchor }) => {
    if (!value) return null;

    if (needsRotatedLabels) {
      // Rotate 45° for many items
      return (
        <g transform={`translate(${x},${y + 5}) rotate(-45 0 0)`}>
          <text
            x={0}
            y={0}
            textAnchor="end"
            style={{
              fill: '#333333',
              fontSize: '0.75rem',
              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
            }}
          >
            {value}
          </text>
        </g>
      );
    } else {
      // Multi-line word wrap for fewer items
      const lines = splitTextOnWords(String(value), 14);
      return (
        <g transform={`translate(${x},${y + 16})`}>
          {lines.map((line, i) => (
            <text
              key={i}
              x={0}
              y={i * 14}
              textAnchor={textAnchor || 'middle'}
              style={{
                fill: '#333333',
                fontSize: '0.8rem',
                fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
              }}
            >
              {line}
            </text>
          ))}
        </g>
      );
    }
  };

  // Determine margins based on title, labels, and rotation
  const topMargin = title ? 80 : 50;
  const bottomMargin = needsRotatedLabels ? 120 : 80;

  // Determine if we should show a legend (either default or custom category legend)
  const hasLegend = showLegend || categoryLegend;

  return (
    <div>
      {/* Export controls */}
      <div className="flex justify-end mb-2">
        <ExportPopover
          onCopyImage={copyImageToClipboard}
          onCopyData={copyDataToClipboard}
          onExportPNG={exportChartAsPNG}
          onExportExcel={exportChartToExcel}
        />
      </div>
      <div ref={chartRef} className="w-full relative" style={{ height: 420 }}>
        <ResponsiveBar
        data={data}
        keys={keys}
        indexBy={indexBy}
        layout={layout}
        groupMode={groupMode}
        minValue={minValue}
        maxValue={computedMaxValue}
        margin={{ top: topMargin + (hasLegend ? 30 : 0), right: 30, bottom: bottomMargin, left: 70 }}
        padding={0.3}
        innerPadding={3}
        colors={resolveColors()}
        colorBy="id"
        borderWidth={2}
        borderRadius={3}
        borderColor={{
          from: 'color',
          modifiers: [['brighter', '0.3']],
        }}
        isInteractive={false}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: xAxisLabel,
          legendPosition: 'middle',
          legendOffset: needsRotatedLabels ? 100 : 60,
          renderTick: axisBottomTick,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: yAxisLabel || 'Value',
          legendPosition: 'middle',
          legendOffset: -50,
          tickValues: 5,
        }}
        label={null}
        theme={CHART_THEME}
        legends={(showLegend && !categoryLegend) ? [
          {
            dataFrom: 'keys',
            anchor: 'top',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -30,
            itemsSpacing: 20,
            // Dynamic width based on longest key name (~8px per char + symbol)
            itemWidth: Math.max(80, Math.max(...keys.map(k => k?.length || 0)) * 8 + 30),
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 14,
            symbolShape: 'circle',
          },
        ] : []}
        layers={[
          titleLayer,
          gridLayer,
          'bars',
          labelsLayer,
          'axes',
          'markers',
          'legends',
          categoryLegendLayer,
          tooltipLayer,
        ]}
      />
      </div>
    </div>
  );
}
